import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, Ban, Save } from 'lucide-react';
import { formatCATDate } from '../../utils/timezone';
import { getClaimCooldownInfo } from './utils';

export default function ClaimCreationCard({
  selectedPatient, isSuperAdmin,
  patientBypassStates, onToggleBypass,
  itemSearchQuery, setItemSearchQuery, filteredPriceList,
  claimItems, setClaimItems, onRemoveItem,
  formatCurrency, getTotalClaimCost, getTotalClaimCostUSD, toLocalBalance,
  onSubmitClaim, loading,
  claimDrafts, maxDrafts, onOpenDrafts, onSaveDraft,
}) {
  const cooldownInfo = getClaimCooldownInfo(selectedPatient.last_claim_date);
  const bypassEnabled = patientBypassStates[selectedPatient.serial_number] || false;
  const canProceedWithClaim = cooldownInfo.canCreateClaim || bypassEnabled || selectedPatient.override_cooldown;

  if (!canProceedWithClaim) {
    return (
      <Card className="border-orange-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200">
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <FileText className="w-5 h-5" />
            Next Treatment Date
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-100 mb-4">
              <span className="text-3xl">🗓️</span>
            </div>
            <h3 className="text-xl font-bold text-orange-800 mb-2">
              Next Treatment: {cooldownInfo.nextTreatmentDate}
            </h3>
            <p className="text-sm text-gray-500 mb-2">
              ({cooldownInfo.daysRemaining} days, {cooldownInfo.hoursRemaining} hours remaining)
            </p>
            <p className="text-gray-600 mb-4">
              The patient is within the post-treatment observation period. Patient should comeback if needed after 14 days.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 inline-block">
              <p className="text-sm text-orange-700">
                <strong>Patient:</strong> {selectedPatient.first_name} {selectedPatient.last_name}<br/>
                <strong>Last Claim:</strong> {selectedPatient.last_claim_date ? formatCATDate(selectedPatient.last_claim_date) : 'N/A'}<br/>
                <strong>Next Treatment:</strong> {cooldownInfo.cooldownEnd ? formatCATDate(cooldownInfo.cooldownEnd) : 'N/A'}
              </p>
            </div>
            {isSuperAdmin && (
              <div className="mt-6 flex flex-col items-center gap-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onToggleBypass(selectedPatient.serial_number)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${bypassEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    data-testid="bypass-toggle-selected"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${bypassEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className={`text-sm font-medium ${bypassEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                    {bypassEnabled ? 'Bypass ON' : 'Bypass OFF'}
                  </span>
                </div>
                <p className="text-xs text-gray-500">Toggle to allow hospitals to create claims for this patient</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200 shadow-md">
      <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
        <CardTitle className="flex items-center gap-2 text-green-900">
          <FileText className="w-5 h-5" />
          Create New Claim
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-3">
          <div>
            <Label className="text-gray-700 font-medium mb-2 block">Search Items</Label>
            <Input
              data-testid="item-search-input"
              placeholder="Search by Item ID or Item Name to see available items"
              value={itemSearchQuery}
              onChange={(e) => setItemSearchQuery(e.target.value)}
              className="h-11 border-gray-300"
            />
            {filteredPriceList.length === 0 && itemSearchQuery && (
              <p className="text-sm text-gray-500 mt-2">No items found matching "{itemSearchQuery}"</p>
            )}
          </div>

          {itemSearchQuery && filteredPriceList.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">
                  Found {filteredPriceList.length} item(s) matching "{itemSearchQuery}"
                </p>
                <p className="text-xs text-gray-500">Click "Add" to add items to claim</p>
              </div>
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold text-gray-700 uppercase">Item ID</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-700 uppercase">Name</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-700 uppercase">Type</th>
                      <th className="text-right p-3 text-xs font-semibold text-gray-700 uppercase">Cost</th>
                      <th className="text-center p-3 text-xs font-semibold text-gray-700 uppercase">Qty</th>
                      <th className="text-center p-3 text-xs font-semibold text-gray-700 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPriceList.map((item) => (
                      <tr key={item.item_id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                        <td className="p-3 text-sm text-gray-700 font-mono">{item.item_id}</td>
                        <td className="p-3 text-sm text-gray-700">{item.item_name}</td>
                        <td className="p-3 text-sm text-gray-600">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.item_type === 'Drug' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {item.item_type}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-right font-semibold">{formatCurrency(item.cost)}</td>
                        <td className="p-3 text-center">
                          <Input
                            type="number"
                            min="1"
                            defaultValue="1"
                            className="h-9 w-16 text-center border-gray-300 text-sm"
                            id={`qty-${item.item_id}`}
                          />
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            onClick={() => {
                              if (claimItems.some(ci => ci.item_id === item.item_id)) {
                                toast.error(`❌ This drug/service is already added: ${item.item_name}`);
                                return;
                              }
                              const qtyInput = document.getElementById(`qty-${item.item_id}`);
                              const quantity = parseInt(qtyInput.value) || 1;
                              setClaimItems([...claimItems, { ...item, quantity }]);
                              qtyInput.value = '1';
                              setItemSearchQuery('');
                              toast.success(`✅ Added ${quantity}x ${item.item_name} to claim`);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {claimItems.length > 0 && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full" data-testid="claim-items-table">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Item</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Type</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Unit Cost</th>
                    <th className="text-center p-3 text-sm font-semibold text-gray-700">Quantity</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Total</th>
                    <th className="text-center p-3 text-sm font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {claimItems.map((item, index) => (
                    <tr key={`${item.item_id || item.item_name}-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3 text-sm text-gray-800">{item.item_name}</td>
                      <td className="p-3 text-sm text-gray-600">{item.item_type}</td>
                      <td className="p-3 text-sm text-gray-600 text-right">{formatCurrency(item.cost)}</td>
                      <td className="p-3 text-sm text-gray-800 text-center font-medium">{item.quantity || 1}</td>
                      <td className="p-3 text-sm text-gray-800 text-right font-bold">{formatCurrency(item.cost * (item.quantity || 1))}</td>
                      <td className="p-3 text-center">
                        <Button
                          data-testid={`remove-item-${index}`}
                          onClick={() => onRemoveItem(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
                <div className="text-xl font-bold text-gray-800">
                  Total: <span data-testid="total-claim-cost" className="text-green-600">{formatCurrency(getTotalClaimCost())}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={onOpenDrafts}
                    className="h-12 px-4 border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Drafts ({claimDrafts.length})
                  </Button>
                  <Button
                    data-testid="submit-claim-button"
                    onClick={onSubmitClaim}
                    disabled={loading || getTotalClaimCostUSD() > selectedPatient.remaining_balance}
                    className="h-12 px-8 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit Claim
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={onSaveDraft}
                    disabled={claimDrafts.length >= maxDrafts}
                    variant="outline"
                    className="h-9 px-4 border-blue-300 text-blue-600 hover:bg-blue-100"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save as Draft
                  </Button>
                  <span className="text-xs text-blue-600">
                    {claimDrafts.length}/{maxDrafts} drafts
                  </span>
                </div>
              </div>

              {getTotalClaimCostUSD() > selectedPatient.remaining_balance && (
                <div className="p-4 bg-red-50 border border-red-300 rounded-lg flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">⚠️ Insufficient Balance</p>
                    <p className="text-xs text-red-700 mt-1">
                      Claim total ({formatCurrency(getTotalClaimCost())}) exceeds available family balance ({formatCurrency(toLocalBalance(selectedPatient.remaining_balance))}).
                      Please reduce the claim amount or contact the family to recharge their balance.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
